// Shared types + helpers for the Flow2 inline Goals table (FF GOALS_VIEW_REVAMP_FE_AEX-3428).
//
// This module deliberately MIRRORS the create/edit save logic in
// components/organisms/GoalDrawer/index.tsx (submitFunds / handleEditClick) so the
// backend payloads and downstream plan-staleness behaviour stay byte-for-byte
// identical between the legacy drawer flow and the new inline table flow. The
// drawer file is intentionally left untouched (per the "new separate files" ask).
import dayjs, { Dayjs } from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { createSelector } from '@reduxjs/toolkit';
import {
    DEFAULT_GOAL_CATEGORY,
    DEFAULT_GOAL_CATEGORY_ORDER,
    DEFAULT_PLANNING_AGE,
    DEFAULT_RETIREMENT_AGE,
    EWS_DURATION_KEYS,
    EWS_DURATION_MAP,
    EWS_TERM_TAB_KEY_MAP,
    FEATURE_NAMES,
    FLOW2_CATEGORY_GOALS,
    GOAL_TENURE_CATEGORIES,
    GoalTenureCategory,
    INCOME_GOAL,
    RETIREMENT_GOAL,
    STATUSVARIANTS,
    TermTabType,
    WEALTH_GOAL
} from '../../common/constants';
import { addAgeToDate, convertDateFormat, convertDateFormatReverse, getYYYYMMDDfromDate } from '../../common/helper';
import {
    calculateYearsBetweenDates,
    checkFFEnabled,
    deepEqual,
    getAffectedEwsTerms,
    pruneGoalFromEwsState,
    removeNonNumeric
} from '../../utils';
import { createGoal, getGoalCategory, saveGoal } from '../../features/client-goals';
import {
    setAffectedEwsTerms,
    setEwsMaps,
    setEwsStepperCtaStatus,
    computeEwsStepperCtaStatus,
    updateGoalContinuity,
    ONBOARDING_BASELINE_CONTINUITY_GOAL
} from '../../features/ews';
import type { AdviceContinuityGoal } from '../../features/ews';
import store from '../../redux/store';
import { setEnableConfirmConsoleNavigation } from '../../features/client';
import { updatePlanState } from '../../features/global/globalSlice';

export type GoalRowType = 'income' | 'wealth' | 'retirement';

/** A single flattened goal row rendered in the inline table (goal.data + goalId). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface GoalInlineRow {
    goalId: string;
    /** 'income' | 'wealth' | 'retirement' */
    type: GoalRowType;
    goalName: string;
    goalPriority?: string;
    /** Wealth/Income main amount; for Retirement this is the (optional) legacy/bequest amount. */
    goalAmount?: number | null;
    /** Retirement main withdrawal/income amount (sent to runpipe). */
    targetedRetirementIncome?: number | null;
    /** 'Annually' | 'Monthly' (income/retirement). Undefined for wealth (one-time). */
    frequency?: string;
    /** Dates are held in the row in the form format 'MM-DD-YYYY'; persisted as 'DD-MM-YYYY'. */
    incomeStartDate?: string | Dayjs | null;
    targetDate?: string | Dayjs | null;
    planStartRetirement?: string | Dayjs | null;
    planStartRetirementKey?: string;
    targetDateKey?: string;
    /** Transient UI selection for the retirement Goal Date dropdown: 'client' | 'coclient' | 'custom'. */
    retirementDateOption?: RetirementDateOption;
    goalCategory?: string | null;
    goalCategoryOrder?: number | null;
    wealthGoalType?: string;
    /** AEX-3324/AEX-3466: set when the goal was saved with the plan; unset = newly added. */
    tenureLocked?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

export type RetirementDateOption = 'client' | 'coclient' | 'custom';

/** Thrown by saveInlineGoal when a duplicate goal name is detected; surfaced on the name field. */
export const DUPLICATE_GOAL_NAME_ERROR = 'DUPLICATE_GOAL_NAME';

const GOAL_TYPE_CONSTANT: Record<GoalRowType, string> = {
    income: INCOME_GOAL,
    wealth: WEALTH_GOAL,
    retirement: RETIREMENT_GOAL
};

export const getGoalMeta = (type: GoalRowType) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FLOW2_CATEGORY_GOALS.find((g: any) => g.type === type) as
        | {
              type: GoalRowType;
              title: string;
              heading: string;
              icon: string;
              drawerTitle: string;
              backgroundColor: string;
              color: string;
          }
        | undefined;

export const isRetirement = (type?: string) => type === 'retirement';

// ----------------------------------------------------------------------------
// Date helpers — storage is 'DD-MM-YYYY', the RHF row/date-picker uses 'MM-DD-YYYY'.
// ----------------------------------------------------------------------------

/** Persisted 'DD-MM-YYYY' -> row/form 'MM-DD-YYYY' (passthrough if not matching). */
export const toFormDate = (stored?: string | Dayjs | null): string | Dayjs | null | undefined => {
    if (!stored || typeof stored !== 'string') return stored;
    return convertDateFormat(stored);
};

/** Row/form value ('MM-DD-YYYY' string or Dayjs) -> persisted 'DD-MM-YYYY'. */
export const toStoredDate = (value?: string | Dayjs | null): string | undefined => {
    if (!value) return value as undefined;
    return typeof value === 'string' ? convertDateFormatReverse(value) : getYYYYMMDDfromDate(value);
};

/** Robustly parse a row/form date value ('MM-DD-YYYY' string or Dayjs) into a Dayjs. */
export const toDayjs = (value?: string | Dayjs | null): Dayjs => {
    if (!value) return dayjs(NaN);
    if (dayjs.isDayjs(value)) return value;
    return dayjs(value as string, 'MM-DD-YYYY');
};

/** Format a row/form date value as 'MMM YYYY' (or '-' when empty/invalid). */
export const formatMonthYear = (value?: string | Dayjs | null): string => {
    const d = toDayjs(value ?? null);
    return d.isValid() ? d.format('MMM YYYY') : '-';
};

// ----------------------------------------------------------------------------
// AEX-3430: past goal-date detection. A plan reopened after its goal timeline dates have
// elapsed must flag those goals. A date is "past" when it falls before today, matching the
// edit-mode future-date validator (`futureDate`) in cells/shared.tsx so read/edit stay in sync.
// ----------------------------------------------------------------------------

const isDateInPast = (d: Dayjs): boolean => d.isValid() && d.isBefore(dayjs(), 'day');

/** Persisted goal (dates stored 'DD-MM-YYYY') with any timeline date in the past. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hasPastGoalDate = (data: any): boolean =>
    [data?.targetDate, data?.incomeStartDate, data?.planStartRetirement].some(
        (stored) => !!stored && isDateInPast(dayjs(stored as string, 'DD-MM-YYYY'))
    );

/** Inline row (dates in form 'MM-DD-YYYY' or Dayjs) with any timeline date in the past. */
export const rowHasPastGoalDate = (
    row: Pick<GoalInlineRow, 'targetDate' | 'incomeStartDate' | 'planStartRetirement'>
): boolean =>
    [row?.targetDate, row?.incomeStartDate, row?.planStartRetirement].some(
        (value) => !!value && isDateInPast(toDayjs(value ?? null))
    );

/** A saved plan's persisted start date that has already elapsed (even by a day) can't be edited. */
export const isPastStartDateLocked = (
    startDate: string | Dayjs | null | undefined,
    isPlanSavedOnce: boolean
): boolean => isPlanSavedOnce && !!startDate && isDateInPast(toDayjs(startDate));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectIsPlanSavedOnce = (state: any): boolean => Boolean(state?.adviceGoals?.global?.isPlanSavedOnce);

// ----------------------------------------------------------------------------
// Retirement date derivation (Client / Co-Client retirement & planning dates).
// ----------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hasCoClient = (adviceClient: any): boolean => !!adviceClient?.relationship?.[0]?.age;

/** Client & Co-Client retirement/planning dates in the row/form format ('MM-DD-YYYY'). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRetirementDerivedDates = (adviceClient: any) => {
    const rel = adviceClient?.relationship?.[0];
    return {
        client: {
            start: toFormDate(
                addAgeToDate(adviceClient?.clientDob, adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE)
            ),
            end: toFormDate(addAgeToDate(adviceClient?.clientDob, adviceClient?.planningAge || DEFAULT_PLANNING_AGE))
        },
        coclient: rel?.age
            ? {
                  start: toFormDate(addAgeToDate(rel?.age, rel?.retirementAge || DEFAULT_RETIREMENT_AGE)),
                  end: toFormDate(addAgeToDate(rel?.age, rel?.planningAge || DEFAULT_PLANNING_AGE))
              }
            : null
    };
};

/** Infer which retirement Goal-Date dropdown option a persisted goal corresponds to. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const resolveRetirementDateOption = (row: GoalInlineRow, adviceClient: any): RetirementDateOption => {
    const derived = getRetirementDerivedDates(adviceClient);
    const start = toDayjs(row.planStartRetirement ?? null);
    const end = toDayjs(row.targetDate ?? null);
    const matches = (a: Dayjs, b?: string | Dayjs | null) => {
        const bd = toDayjs(b ?? null);
        return a.isValid() && bd.isValid() && a.isSame(bd, 'month');
    };
    if (matches(start, derived.client.start) && matches(end, derived.client.end)) return 'client';
    if (derived.coclient && matches(start, derived.coclient.start) && matches(end, derived.coclient.end))
        return 'coclient';
    return 'custom';
};

// ----------------------------------------------------------------------------
// Row <-> persisted goal mapping.
// ----------------------------------------------------------------------------

/** Map a persisted formFields[] entry to a flat inline row (dates -> form format). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapPersistedGoalToRow = (formField: any, adviceClient: any): GoalInlineRow => {
    const data = formField?.data ?? {};
    const type = (data.type as GoalRowType) || 'wealth';
    const row: GoalInlineRow = {
        ...data,
        goalId: formField.goalId ?? data.goalId ?? '',
        type,
        goalName: data.goalName ?? '',
        goalPriority: data.goalPriority,
        goalAmount: data.goalAmount,
        targetedRetirementIncome: data.targetedRetirementIncome,
        frequency: data.frequency,
        incomeStartDate: toFormDate(data.incomeStartDate),
        targetDate: toFormDate(data.targetDate),
        planStartRetirement: toFormDate(data.planStartRetirement),
        planStartRetirementKey: data.planStartRetirementKey,
        targetDateKey: data.targetDateKey,
        goalCategory: data.goalCategory,
        goalCategoryOrder: data.goalCategoryOrder,
        wealthGoalType: data.wealthGoalType
    };
    if (type === 'retirement') {
        row.retirementDateOption = resolveRetirementDateOption(row, adviceClient);
    }
    return row;
};

/** Seed defaults for a brand-new inline row of the given type. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const buildNewRowDefaults = (type: GoalRowType): GoalInlineRow => {
    const base: GoalInlineRow = {
        goalId: '',
        type,
        goalName: '',
        goalPriority: undefined,
        goalAmount: type === 'retirement' ? 0 : null,
        frequency: undefined,
        targetDate: null,
        goalCategory: null,
        goalCategoryOrder: null
    };
    if (type === 'income') base.incomeStartDate = null;
    if (type === 'retirement') {
        base.targetedRetirementIncome = undefined;
        base.planStartRetirement = null;
        base.planStartRetirementKey = '';
        base.targetDateKey = '';
    }
    return base;
};

// ----------------------------------------------------------------------------
// Tenure (goalCategory / order). Backend-driven via getGoalCategory; retirement = Long.
// ----------------------------------------------------------------------------

export interface GoalTenure {
    goalCategory: string | null;
    goalCategoryOrder: number | null;
}

// AEX-3108: only multiportfolio tenants (Banorte) bucket goals into Short/Medium/Long terms.
const isMultiPortfolioTenant = (): boolean => Boolean(store.getState()?.global?.globalConfig?.isMultiPortfolioTenant);

export const RETIREMENT_TENURE: GoalTenure = {
    goalCategory: EWS_DURATION_MAP.longTerm,
    goalCategoryOrder: TermTabType.LONG + 1
};

/**
 * Compute a goal's tenure bucket. Retirement is always Long. For income/wealth we
 * call the same generategoalcategory endpoint the drawer uses; returns nulls when the
 * required target date is missing/invalid or the request fails.
 */
export const computeGoalTenure = async (
    type: GoalRowType,
    row: GoalInlineRow,
    language?: string
): Promise<GoalTenure> => {
    // AEX-3108: single-portfolio tenants have no terms; skip generategoalcategory and use the default key.
    if (!isMultiPortfolioTenant()) {
        return { goalCategory: DEFAULT_GOAL_CATEGORY, goalCategoryOrder: DEFAULT_GOAL_CATEGORY_ORDER };
    }
    if (type === 'retirement') return { ...RETIREMENT_TENURE };
    const target = toDayjs(row.targetDate ?? null);
    if (!target.isValid()) return { goalCategory: null, goalCategoryOrder: null };
    // generategoalcategory is always called without goalStartDate, so the backend categorizes
    // against the current date.
    const goalObj = {
        goalName: 'currentGoal',
        goalTargetDate: target.format('DD-MM-YYYY')
    };
    try {
        const response = await getGoalCategory({ language: language || 'en', goals: [goalObj] });
        const current = response?.goalCategoryResult?.currentGoal;
        if (current) return { goalCategory: current.value, goalCategoryOrder: current.order };
    } catch (error) {
        console.error('Error fetching inline goal category:', error);
    }
    return { goalCategory: null, goalCategoryOrder: null };
};

// ----------------------------------------------------------------------------
// AEX-3324: target-date tenure lock. Once a plan is saved (global.isPlanSavedOnce), an existing
// goal's target date is restricted to the date range that preserves its saved tenure category, so
// editing the date can't move the goal into a different term. Mirrors the backend month-gap logic
// used by generategoalcategory (the target date is treated as the 1st of the month).
// ----------------------------------------------------------------------------

// Match on the leading Short/Medium/Long token so 'Short Term' and 'Short Term Goal' both resolve.
const findTenureCategory = (categoryValue?: string | null): GoalTenureCategory | undefined => {
    if (!categoryValue) return undefined;
    const token = categoryValue.trim().toLowerCase().split(' ')[0];
    return GOAL_TENURE_CATEGORIES.find((c) => c.value.toLowerCase().split(' ')[0] === token);
};

/**
 * Target-date window (1st-of-month bounds) that keeps a goal in `categoryValue` for a tenure that
 * begins at `start`. `maxDate` is undefined for the open-ended long-term bucket.
 */
export const getGoalTenureWindow = (
    categoryValue: string | null | undefined,
    start: Dayjs
): { minDate: Dayjs; maxDate?: Dayjs } | null => {
    // AEX-3108: single-portfolio tenants don't restrict a target date to a term range.
    if (!isMultiPortfolioTenant()) return null;
    const category = findTenureCategory(categoryValue);
    if (!category || !start.isValid()) return null;
    // The backend drops a month when the target day precedes the start day; the target is always the
    // 1st, so that adjustment applies whenever the start date falls after the 1st of its month.
    const dayAdjustment = start.date() > 1 ? 1 : 0;
    const base = start.startOf('month');
    const minMonths = category.minInclusive ? category.minYears * 12 : category.minYears * 12 + 1;
    const minDate = base.add(minMonths + dayAdjustment, 'month');
    let maxDate: Dayjs | undefined;
    if (Number.isFinite(category.maxYears)) {
        const maxMonths = category.maxInclusive ? category.maxYears * 12 : category.maxYears * 12 - 1;
        maxDate = base.add(maxMonths + dayAdjustment, 'month');
    }
    return { minDate, maxDate };
};

/**
 * Picker min/max ('...format()' strings) for a locked goal's target date, or null when no lock
 * applies (tenure not locked yet, or a brand-new/uncategorized goal). `planStartDate` is the
 * saved-plan date the backend categorized against (global.planCreationDate).
 */
export const getTenureLockWindow = (
    row: GoalInlineRow,
    isTenureLocked: boolean,
    planStartDate?: string | null
): { startDate: string; endDate?: string } | null => {
    if (!isMultiPortfolioTenant() || !isTenureLocked || !row?.goalCategory) return null;
    const range = getGoalTenureWindow(row.goalCategory, planStartDate ? dayjs(planStartDate) : dayjs());
    if (!range) return null;
    let { minDate, maxDate } = range;
    // Never let the already-saved target fall outside its own window (guards against small start-date
    // drift between when the category was set and the stored plan-creation date).
    const current = toDayjs(row.targetDate ?? null);
    if (current.isValid()) {
        if (current.isBefore(minDate, 'month')) minDate = current.startOf('month');
        if (maxDate && current.isAfter(maxDate, 'month')) maxDate = current.startOf('month');
    }
    return {
        startDate: minDate.format(),
        endDate: maxDate ? maxDate.format() : undefined
    };
};

// Memoized on the formFields reference so calling isGoalTenureLocked per row (from selectors/render)
// rescans the goals at most once per change instead of on every call — avoids O(n^2) across the table.
const selectHasTenureStamps = createSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (adviceGoals: any) => adviceGoals?.formFields as any[] | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (formFields) => (formFields ?? []).some((f: any) => f?.data?.tenureLocked)
);

/**
 * Whether a goal's tenure is locked because it was saved with the plan. Locked goals keep their
 * term and must not re-hit generategoalcategory. Plans saved before tenure-stamping shipped have no
 * per-goal stamp, so they fall back to the plan-level isPlanSavedOnce.
 */
export const isGoalTenureLocked = (
    row: Pick<GoalInlineRow, 'tenureLocked'>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adviceGoals: any
): boolean =>
    isMultiPortfolioTenant() &&
    (selectHasTenureStamps(adviceGoals) ? !!row.tenureLocked : !!adviceGoals?.global?.isPlanSavedOnce);

/**
 * Stamp every goal in a plan's formFields as tenure-locked. Called when the plan is saved so all
 * saved goals become locked; goals added afterwards stay unstamped until the next save. Returns a
 * new array (does not mutate the input).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const stampGoalsTenureLocked = (formFields: any[]): any[] =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (formFields ?? []).map((f: any) => ({ ...f, data: { ...(f?.data ?? {}), tenureLocked: true } }));

// ----------------------------------------------------------------------------
// Persisted-data builder (normalizes currency -> number, date -> 'DD-MM-YYYY').
// ----------------------------------------------------------------------------

const normalizeCurrency = (value: unknown) => (value ? Number(removeNonNumeric(value)) : value);

/** Build the goal.data payload for a row, mirroring the drawer's field normalization. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildPersistedData = (row: GoalInlineRow): Record<string, any> => {
    const type = row.type;
    const meta = getGoalMeta(type);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const common: Record<string, any> = {
        goalName: row.goalName,
        goalPriority: row.goalPriority,
        goalType: GOAL_TYPE_CONSTANT[type],
        wealthGoalType: meta?.title,
        type,
        sources: { incomeSource: [], investmentSource: [] }
    };
    if (type === 'income') {
        return {
            ...common,
            goalAmount: normalizeCurrency(row.goalAmount),
            frequency: row.frequency,
            incomeStartDate: toStoredDate(row.incomeStartDate),
            targetDate: toStoredDate(row.targetDate)
        };
    }
    if (type === 'wealth') {
        return {
            ...common,
            goalAmount: normalizeCurrency(row.goalAmount),
            targetDate: toStoredDate(row.targetDate)
        };
    }
    // retirement
    const planStartRetirement = toStoredDate(row.planStartRetirement);
    const targetDate = toStoredDate(row.targetDate);
    return {
        ...common,
        targetedRetirementIncome: normalizeCurrency(row.targetedRetirementIncome),
        frequency: row.frequency,
        planStartRetirement,
        targetDate,
        planStartRetirementKey: row.planStartRetirementKey || '',
        targetDateKey: row.targetDateKey || '',
        yearsInRetirement: calculateYearsBetweenDates(planStartRetirement, targetDate ?? ''),
        goalAmount: normalizeCurrency(row.goalAmount) // legacy / bequest (optional)
    };
};

// ----------------------------------------------------------------------------
// Save (create + edit) — mirrors submitFunds / handleEditClick.
// ----------------------------------------------------------------------------

export interface SaveGoalContext {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adviceGoals: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adviceClient: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    orgInfo: any;
    isFlow2: boolean;
    language?: string;
    // Per-term EWS maps from the dedicated ews slice.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ewsRequests?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ewsResponses?: any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isDuplicateGoalName = (adviceGoals: any, goalName?: string, selfGoalId?: string) =>
    (adviceGoals?.formFields ?? [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((goal: any) => (selfGoalId ? goal.goalId !== selfGoalId : true))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .some((goal: any) => goal.data?.goalName?.toLowerCase() === goalName?.toLowerCase());

/** Minimal, permissive view of the goals slice for detecting offloaded (reference-id) run artifacts. */
type RunArtifactBearingGoals = {
    request?: unknown;
    response?: unknown;
    global?: {
        responseGoalCalculator?: unknown;
        ewsRequests?: unknown;
        ewsResponses?: unknown;
    } | null;
};

/**
 * Whether the plan's run artifacts have been offloaded by the backend to opaque reference-id
 * strings. A live plan holds runpipe request/response, EWS and Goal Calculator results as inline
 * objects; once Plan Execution runs the backend replaces each with an id string, and echoing those
 * back crashes updateproposals ("Struct must be in a dict which is <id> at Struct").
 */
export const hasOffloadedRunArtifacts = (goals?: RunArtifactBearingGoals | null): boolean => {
    const isRef = (value: unknown) => typeof value === 'string';
    const global = goals?.global;
    const refArrays = [goals?.request, goals?.response, global?.responseGoalCalculator];
    if (refArrays.some((arr) => Array.isArray(arr) && arr.some(isRef))) return true;
    return [global?.ewsRequests, global?.ewsResponses].some(
        (dict) => !!dict && typeof dict === 'object' && Object.values(dict).some(isRef)
    );
};

/**
 * Persist an inline goal (create when isEdit is false, edit otherwise). Throws
 * DUPLICATE_GOAL_NAME_ERROR when the name collides so the caller can keep the row open.
 */
export const saveInlineGoal = async (
    row: GoalInlineRow,
    isEdit: boolean,
    tenure: GoalTenure,
    ctx: SaveGoalContext
): Promise<void> => {
    const { dispatch, adviceGoals, adviceClient, orgInfo, isFlow2, ewsRequests, ewsResponses } = ctx;
    const ff3021 = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']);
    const ff3021Id = checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']);
    const persisted = buildPersistedData(row);

    if (isDuplicateGoalName(adviceGoals, row.goalName, isEdit ? row.goalId : undefined)) {
        throw new Error(DUPLICATE_GOAL_NAME_ERROR);
    }

    const originalGoalsData = JSON.parse(JSON.stringify(adviceGoals));
    const updatedGoalsData = JSON.parse(JSON.stringify(adviceGoals));

    if (!isEdit) {
        const generatedId = uuidv4();
        const nextIndex = !adviceGoals?.formFields ? 0 : adviceGoals.formFields.length + 1;
        const newFormField = {
            goalId: generatedId,
            data: {
                ...persisted,
                index: nextIndex,
                // Continuity of Advice (flow2): last recommended portfolio id, populated after a successful EWS run
                ...(checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603']) &&
                    isFlow2 && {
                        lastRecommendedPortfolioId: null
                    }),
                ...((ff3021Id || !isFlow2) && { goalId: generatedId }),
                ...(!ff3021 && { createdAt: new Date().toISOString() }),
                goalCategory: tenure.goalCategory,
                goalCategoryOrder: tenure.goalCategoryOrder
            }
        };
        updatedGoalsData.formFields = updatedGoalsData?.formFields
            ? [...updatedGoalsData.formFields, newFormField]
            : [newFormField];
        if (ff3021) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            updatedGoalsData.formFields.forEach((item: any) => {
                if (item.goalId !== generatedId && !item.data['isNew'] && item.data['update'] !== true) {
                    item.data['update'] = false;
                }
            });
        }
        // AEX-3603 #8: a brand-new goal is new on every axis (onboarding baseline).
        if (checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603'])) {
            dispatch(
                updateGoalContinuity({ goalId: generatedId, changes: { ...ONBOARDING_BASELINE_CONTINUITY_GOAL } })
            );
        }
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const priorGoalData = adviceGoals?.formFields?.find((g: any) => g.goalId === row.goalId)?.data;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedGoalsData.formFields.forEach((item: any) => {
            if (item.goalId === row.goalId) {
                item.data = {
                    ...item.data,
                    ...persisted,
                    goalCategory: tenure.goalCategory,
                    goalCategoryOrder: tenure.goalCategoryOrder,
                    ...(ff3021 ? { update: true } : { updatedAt: new Date().toISOString() })
                };
            } else if (ff3021) {
                item.data['update'] = false;
            }
        });

        if (checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603'])) {
            const continuityChanges: Partial<AdviceContinuityGoal> = {};
            if (priorGoalData?.goalPriority !== persisted.goalPriority) {
                continuityChanges.isNewGoalPriority = true;
            }
            if (
                priorGoalData?.goalAmount !== persisted.goalAmount ||
                priorGoalData?.targetedRetirementIncome !== persisted.targetedRetirementIncome
            ) {
                continuityChanges.isNewGoal = true;
            }
            if (
                priorGoalData?.incomeStartDate !== persisted.incomeStartDate ||
                priorGoalData?.targetDate !== persisted.targetDate ||
                priorGoalData?.planStartRetirement !== persisted.planStartRetirement
            ) {
                continuityChanges.isNewInvestmentTenure = true;
            }
            if (Object.keys(continuityChanges).length > 0) {
                dispatch(updateGoalContinuity({ goalId: row.goalId, changes: continuityChanges }));
            }
        }
    }

    updatedGoalsData.domainEmail = orgInfo?.orgEmail ?? '';
    updatedGoalsData.goeConfigEmail = orgInfo?.orgEmail ?? '';
    updatedGoalsData.riskProfile = adviceClient.riskProfile;
    updatedGoalsData.assignedClientId = adviceClient._id;
    updatedGoalsData.error = false;
    updatedGoalsData.loading = false;

    const changed = isEdit ? !deepEqual(originalGoalsData.formFields, updatedGoalsData.formFields) : true;
    if (!changed) return;

    if (
        hasOffloadedRunArtifacts({
            ...updatedGoalsData,
            global: { ...updatedGoalsData.global, ewsRequests, ewsResponses }
        })
    ) {
        // AEX-3552: the plan was executed, so its run artifacts are backend reference-id strings that
        // updateproposals cannot deserialize. Revert to a clean draft (dropping the stale references)
        // so the save succeeds and the pipeline re-runs from scratch.
        updatedGoalsData.request = [];
        updatedGoalsData.response = [];
        // The per-term EWS maps live in the dedicated slice; clear them there so the reconstructed
        // save payload and re-run both start from empty.
        dispatch(setEwsMaps({ requests: {}, responses: {} }));
        dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
        updatedGoalsData.global = {
            ...updatedGoalsData.global,
            ...(isFlow2 && { status: STATUSVARIANTS.INCOMPLETE }),
            enablePDFGenerate: false,
            enablePlanDetails: false,
            responseGoalCalculator: [],
            planExecutionResponse: {},
            planExecutionStatus: 'idle'
        };
    } else {
        if (updatedGoalsData.global?.hasRunPipe) {
            updatedGoalsData.global = { ...updatedGoalsData.global, status: STATUSVARIANTS.INCOMPLETE };
        }
        if (updatedGoalsData.global?.enablePDFGenerate) updatedGoalsData.global.enablePDFGenerate = false;
        if (updatedGoalsData.global?.enablePlanDetails) updatedGoalsData.global.enablePlanDetails = false;

        const oldCategory = isEdit ? row.goalCategory : undefined;
        dispatch(
            setAffectedEwsTerms(
                getAffectedEwsTerms(
                    store.getState().ews?.affectedEwsTerms ?? [],
                    tenure.goalCategory,
                    oldCategory ?? undefined
                )
            )
        );
    }

    if (!updatedGoalsData._id) {
        await dispatch(createGoal(updatedGoalsData)).unwrap();
    } else {
        await dispatch(saveGoal(updatedGoalsData));
    }

    if (adviceGoals?.global?.isPlanRun) {
        dispatch(setEnableConfirmConsoleNavigation(true));
    }
    dispatch(updatePlanState({ key: 'isPlanOpen', value: true }));
};

// ----------------------------------------------------------------------------
// Delete — mirrors GoalSummaryChipFlow2.onConfirmDelete (surgical prune + saveGoal).
// ----------------------------------------------------------------------------

export const deleteInlineGoal = (
    goalId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adviceGoals: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch: any,
    // Per-term EWS maps from the dedicated ews slice.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ewsRequests?: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ewsResponses?: any
): void => {
    const ff3021 = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const remainingFormFields = adviceGoals.formFields.filter((goal: any) => goal.goalId !== goalId);
    // Derive the deleted goal's EWS term so remaining goals in that term are re-evaluated: deleting a
    // goal frees its funding sources, which changes the funding math for the other goals in the same
    // term, so that term must be flagged affected (mirrors the ADD/EDIT flow's getAffectedEwsTerms).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deletedGoal = adviceGoals.formFields.find((goal: any) => goal.goalId === goalId);
    const deletedGoalOrder = deletedGoal?.data?.goalCategoryOrder;
    // Resolve the deleted goal's EWS term from its order, falling back to its stored goalCategory
    // when the order is missing so the term is still re-evaluated instead of silently skipped.
    const deletedGoalTerm = !isMultiPortfolioTenant()
        ? DEFAULT_GOAL_CATEGORY
        : typeof deletedGoalOrder === 'number'
        ? EWS_DURATION_MAP[EWS_TERM_TAB_KEY_MAP[(deletedGoalOrder - 1) as TermTabType]]
        : deletedGoal?.data?.goalCategory ?? null;
    // Only flag the term affected while goals remain in it: an emptied term has no funding math left
    // to re-evaluate, so re-running EWS for it would be wasted work.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const termStillHasGoals = remainingFormFields.some((goal: any) =>
        typeof deletedGoalOrder === 'number'
            ? goal?.data?.goalCategoryOrder === deletedGoalOrder
            : goal?.data?.goalCategory === deletedGoalTerm
    );
    const deletedGoalCategory = termStillHasGoals ? deletedGoalTerm : null;
    const affectedEwsTerms = getAffectedEwsTerms(store.getState().ews?.affectedEwsTerms ?? [], deletedGoalCategory);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const markUpdated = (goal: any) => (goal.goalId ? { ...goal, data: { ...goal.data, update: false } } : goal);
    const formFieldsForDelete = ff3021 ? remainingFormFields.map(markUpdated) : remainingFormFields;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const remainingRequest = (adviceGoals.request ?? []).filter((item: any) => item?.goalId !== goalId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const remainingResponse = (adviceGoals.response ?? []).filter((item: any) => item?.goalId !== goalId);
    const remainingResponseGoalCalculator = (adviceGoals.global?.responseGoalCalculator ?? []).filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => item?.goalId !== goalId
    );
    const { ewsRequests: remainingEwsRequests, ewsResponses: remainingEwsResponses } = pruneGoalFromEwsState(
        ewsRequests,
        ewsResponses,
        goalId
    );
    // Update the per-term EWS maps + affected-terms flag in the dedicated slice; saveGoal
    // reconstructs global.ews*/affectedEwsTerms from this slice, so the persisted document stays in sync.
    dispatch(setEwsMaps({ requests: remainingEwsRequests, responses: remainingEwsResponses }));
    // Keep the aggregate stepper/CTA rollup consistent with the pruned responses.
    dispatch(setEwsStepperCtaStatus(computeEwsStepperCtaStatus(remainingEwsResponses)));
    dispatch(setAffectedEwsTerms(affectedEwsTerms));
    const updatedGoalData = {
        ...adviceGoals,
        formFields: formFieldsForDelete,
        request: remainingRequest,
        response: remainingResponse,
        global: {
            ...adviceGoals.global,
            responseGoalCalculator: remainingResponseGoalCalculator
        }
    };
    dispatch(saveGoal(updatedGoalData));
};

// ----------------------------------------------------------------------------
// Read-only display formatters (Figma inline table).
// ----------------------------------------------------------------------------

/** Main displayed amount: retirement -> targetedRetirementIncome, else goalAmount. */
export const getMainAmount = (row: GoalInlineRow): number =>
    Number((row.type === 'retirement' ? row.targetedRetirementIncome : row.goalAmount) || 0);

/** Retirement legacy/bequest amount (only shown when > 0). */
export const getLegacyAmount = (row: GoalInlineRow): number =>
    row.type === 'retirement' ? Number(row.goalAmount || 0) : 0;

/** Frequency label for read-only cells: wealth -> One-time, else Monthly/Annual. */
// eslint-disable-next-line no-unused-vars
export const getFrequencyLabel = (row: GoalInlineRow, t: (k: string) => string): string => {
    if (row.type === 'wealth') return t('GOALS_INLINE_ONE_TIME');
    return row.frequency === 'Monthly' ? t('MONTHLY') : t('ANNUAL');
};

/** Goal Date read-only text per goal type. */
export const formatGoalDateDisplay = (row: GoalInlineRow): string => {
    if (row.type === 'wealth') return formatMonthYear(row.targetDate);
    if (row.type === 'income') return `${formatMonthYear(row.incomeStartDate)} - ${formatMonthYear(row.targetDate)}`;
    return `${formatMonthYear(row.planStartRetirement)} - ${formatMonthYear(row.targetDate)}`;
};
